import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, API_URL, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText } from "lucide-react";

export default function Registry() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const download = async (format) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("vape_token");
      const params = new URLSearchParams({ format });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const resp = await fetch(`${API_URL}/registry/export?${params.toString()}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Échec du téléchargement");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = format === "csv" ? "csv" : "pdf";
      a.href = url; a.download = `registre_dgccrf_${new Date().toISOString().slice(0,10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registre téléchargé");
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout title="Registre DGCCRF">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6 border border-zinc-800 p-8 space-y-5">
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Export du registre de mise en bouteille</div>
            <div className="text-sm text-zinc-400 mt-2 leading-relaxed">
              Le registre reprend chaque étiquette générée avec les informations de traçabilité obligatoires :
              date, marque, saveur, UFI, numéro de lot, DLUO, taille, quantité et client.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Du</label>
              <Input data-testid="registry-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 font-mono" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Au</label>
              <Input data-testid="registry-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 font-mono" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button data-testid="registry-pdf" disabled={loading} onClick={() => download("pdf")}
              className="flex-1 bg-red-500 hover:bg-red-600 rounded-none">
              <FileText className="w-4 h-4 mr-2" /> Télécharger PDF
            </Button>
            <Button data-testid="registry-csv" disabled={loading} onClick={() => download("csv")} variant="outline"
              className="flex-1 rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Télécharger CSV
            </Button>
          </div>
        </div>

        <div className="lg:col-span-6 border border-zinc-800 p-8 bg-zinc-950">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-500">Rappel réglementaire</div>
          <ul className="mt-4 space-y-3 text-sm text-zinc-300 leading-relaxed">
            <li>• Le registre doit être <b>tenu à jour</b> pour chaque mise en bouteille de e-liquide.</li>
            <li>• Il doit être <b>présenté à la DGCCRF</b> en cas de contrôle.</li>
            <li>• Conservez-le pendant <b>5 ans minimum</b>.</li>
            <li>• Chaque lot doit être <b>traçable</b> jusqu'au client final.</li>
          </ul>
          <div className="mt-6 border-t border-zinc-800 pt-4 text-xs font-mono text-zinc-500">
            Astuce : exportez le registre en fin de mois pour votre archivage comptable.
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
